import { brutoCapex, capexLlevaIvaLocal } from "./brutoCapex";
import { getSaldosPorFacturaProveedor } from "./estadoCuenta/saldosPorFacturaProveedor";
import { getNombreProveedor } from "./proveedores";
import { client } from "./supabase";

// ─── Costos de proyecto de Cartelería (Fase 2 — frontend) ─────────────────────────────────────
// Espejo de lectura/escritura de `costos_proyecto` / `costos_proyecto_cuotas`. Escritura SOLO por
// las RPCs `guardar_costo_proyecto` / `eliminar_costo_proyecto` (el guard de la base rechaza
// cualquier INSERT/UPDATE/DELETE directo — 10.190, ver supabase/migrations/20261003f_*). Diseño y
// contrato completos: docs/decisiones/2026-10-03-costos-carteleria-fase1.md.

export const CONCEPTOS_COSTO_PROYECTO = ["pantalla", "implementacion", "soporte", "instalacion", "materiales", "otro"];

const LABELS_CONCEPTO_COSTO = {
  pantalla: "Pantalla",
  implementacion: "Implementación",
  soporte: "Soporte",
  instalacion: "Instalación",
  materiales: "Materiales",
  otro: "Otro",
};

export const getConceptoCostoLabel = (concepto) => LABELS_CONCEPTO_COSTO[concepto] || concepto || "—";

// Costos vigentes (no eliminados) de un proyecto, con el proveedor embebido (10.7: el vínculo real
// es por proveedor_id; alias/nombre es solo display, getNombreProveedor en src/utils/proveedores.js).
export const getCostosProyecto = async (negocioId) => {
  const { data, error } = await client
    .from("costos_proyecto")
    .select("*, proveedor:proveedores(id, nombre, alias)")
    .eq("negocio_id", String(negocioId))
    .eq("eliminado", false)
    .order("id", { ascending: false });
  if (error) throw error;
  return data || [];
};

// Un costo por id (con proveedor embebido) — usado para el patch puntual tras guardar (10.234):
// nunca se recarga la lista completa del proyecto, solo se vuelve a leer la fila tocada.
export const getCostoProyectoPorId = async (costoId) => {
  const { data, error } = await client
    .from("costos_proyecto")
    .select("*, proveedor:proveedores(id, nombre, alias)")
    .eq("id", costoId)
    .single();
  if (error) throw error;
  return data;
};

// Conteo de cuotas por costo (cualquier plan vigente), para el indicador "plan de cuotas (N)" —
// mismo patrón que `getPlanCountsByActivo` (src/utils/planPagosActivos.js) para Inversión.
export const getCuotaCountsPorCosto = async (costoIds) => {
  if (!costoIds || costoIds.length === 0) return {};
  const { data, error } = await client.from("costos_proyecto_cuotas").select("costo_id").in("costo_id", costoIds);
  if (error) throw error;
  const counts = {};
  for (const row of data || []) counts[row.costo_id] = (counts[row.costo_id] || 0) + 1;
  return counts;
};

// Cuotas de UN costo, ordenadas por numero_cuota — insumo del sub-modal de plan de cuotas.
export const getCuotasDeCosto = async (costoId) => {
  const { data, error } = await client
    .from("costos_proyecto_cuotas")
    .select("*")
    .eq("costo_id", costoId)
    .order("numero_cuota", { ascending: true });
  if (error) throw error;
  return data || [];
};

// RPC `guardar_costo_proyecto` (alta/edición + plan; 10.190/10.259 — máquina 'plan' | 'fecha_unica';
// 'cero' existe en la base pero se rechaza para un costo vigente). `usuarioId` es el id REAL del
// usuario autenticado (10.104) — nunca un placeholder; la RPC hace 0→NULL (NULLIF) si llegara 0.
export const guardarCostoProyecto = async ({
  costoId = null,
  negocioId = null,
  proveedorId,
  concepto,
  descripcion = null,
  monto,
  moneda,
  exentoIva = false,
  estado,
  cuotas = [],
  fechaUnica = null,
  usuarioId = null,
}) => {
  const { data, error } = await client.rpc("guardar_costo_proyecto", {
    p_costo_id: costoId,
    p_negocio_id: negocioId != null ? String(negocioId) : null,
    p_proveedor_id: proveedorId,
    p_concepto: concepto,
    p_descripcion: descripcion,
    p_monto: monto,
    p_moneda: moneda,
    p_exento_iva: exentoIva,
    p_estado: estado,
    p_cuotas: cuotas,
    p_fecha_unica: fechaUnica,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

// RPC `eliminar_costo_proyecto` (soft delete, rechaza doble eliminación — el error de la base se
// muestra tal cual, 10.177).
export const eliminarCostoProyecto = async (costoId) => {
  const { data, error } = await client.rpc("eliminar_costo_proyecto", { p_costo_id: costoId });
  if (error) throw error;
  return data;
};

// Total CON IVA de una línea de costo — ÚNICA fuente: `brutoCapex`/`capexLlevaIvaLocal`
// (src/utils/brutoCapex.js, reglas 10.1/10.19/10.27), la MISMA que usa Inversión de Edificios
// (`ivaLineaInversion` en EdificiosTable/index.js). Nunca recalcular el IVA a mano acá.
export const totalConIvaCosto = (neto, moneda, pais, exentoIva) => {
  const n = Number(neto || 0);
  const aplica = capexLlevaIvaLocal(moneda, pais, exentoIva) && n > 0;
  const total = brutoCapex(n, { exento_iva: exentoIva, moneda, pais });
  return { iva: total - n, total, aplica };
};

// ─── Fase 3 (Futuros "Proyectos", 2026-10-03) ──────────────────────────────────────────────────

// Nombre del proyecto a mostrar (edificio o cliente referenciado, MAYÚSCULAS) — MISMO criterio y
// MISMA prioridad (edificio antes que cliente, por si el id colisiona entre las dos tablas) que
// `getNombreProyecto` de `src/components/CarteleriaList/index.js` (línea ~331), verificado contra el
// código (10.243) — esa función es un closure sobre estado de componente (`edificios`/`clientes`
// maps locales), no exportable; esta es su equivalente puro con los maps como parámetro, para reusar
// el MISMO comportamiento en el loader de Futuros sin un import componente→componente.
export const nombreProyectoDesdeMapas = (referenciaId, edificiosMap, clientesMap) => {
  if (referenciaId == null) return null;
  const edificio = edificiosMap?.[referenciaId];
  if (edificio) return edificio.toUpperCase();
  const cliente = clientesMap?.[referenciaId];
  if (cliente) return cliente.toUpperCase();
  return `ID ${referenciaId}`;
};

// Concepto de una línea de costo de proyecto en el panel Futuros — fuente ÚNICA (10.1), formato
// EXACTO pedido: "Cartelería · <nombre del proyecto> | <concepto> | <proveedor>". `costo.nombreProyecto`
// lo agrega el loader (`getCostosProyectoFuturos`, abajo) vía `nombreProyectoDesdeMapas`; el proveedor
// sale de `getNombreProveedor` (alias || razón social, 10.217/10.1) — requiere que el embed de
// proveedor traiga `alias` (ya lo hace `getCostosProyecto`/`getCostoProyectoPorId` de arriba).
export const construirConceptoCostoProyecto = (costo, nombreProyecto) =>
  [
    `Cartelería · ${nombreProyecto || `Proyecto #${costo?.negocio_id}`}`,
    getConceptoCostoLabel(costo?.concepto),
    getNombreProveedor(costo?.proveedor),
  ]
    .filter(Boolean)
    .join(" | ");

// Loader batcheado de TODOS los costos vigentes (`eliminado=false`) de un país para el panel Futuros
// de FlujoCajaV2 — UNA sola carga, sin N+1 por fila: costos + proveedor embebido (con alias, 10.217)
// en una query, cuotas de TODOS los costos en una segunda query batcheada por `costo_id IN (...)`, y
// los maps de edificios/clientes (por país, 2 queries chicas más — mismo patrón que
// `CarteleriaList.fetchData`) para resolver `nombreProyecto` sin una query por fila. Nunca atrapa el
// error en un catch que devuelva `[]` (10.170/10.177): si cualquiera de las 4 queries falla, propaga
// — el caller (`cargarDatosFuturos`, FlujoCajaV2/index.js) decide el `null`+toast, nunca esta función
// en silencio.
export const getCostosProyectoFuturos = async (pais) => {
  const [costosRes, edificiosRes, clientesRes] = await Promise.all([
    client
      .from("costos_proyecto")
      .select("*, proveedor:proveedores(id, nombre, alias), negocio:negocios_carteleria(id, referencia_id)")
      .eq("pais", pais)
      .eq("eliminado", false),
    client.from("edificios").select("id, edificio").eq("pais", pais),
    client.from("clientes").select("id, nombre").eq("pais", pais),
  ]);
  if (costosRes.error) throw costosRes.error;
  if (edificiosRes.error) throw edificiosRes.error;
  if (clientesRes.error) throw clientesRes.error;

  const costos = costosRes.data || [];
  const edificiosMap = Object.fromEntries((edificiosRes.data || []).map((e) => [e.id, e.edificio]));
  const clientesMap = Object.fromEntries((clientesRes.data || []).map((c) => [c.id, c.nombre]));

  const costoIds = costos.map((c) => c.id);
  let cuotasRes = { data: [] };
  if (costoIds.length > 0) {
    cuotasRes = await client
      .from("costos_proyecto_cuotas")
      .select("*")
      .in("costo_id", costoIds)
      .order("numero_cuota", { ascending: true });
  }
  if (cuotasRes.error) throw cuotasRes.error;

  const cuotasPorCosto = {};
  for (const cu of cuotasRes.data || []) {
    if (!cuotasPorCosto[cu.costo_id]) cuotasPorCosto[cu.costo_id] = [];
    cuotasPorCosto[cu.costo_id].push(cu);
  }

  // Fase 4 (vínculo con documentos, 2026-10-04): Σ NETO vigente por línea — batcheado, una sola
  // query por TODOS los costos del país (10.167: nunca N+1). El remanente real (bruto, repartido
  // oldest-first sobre las cuotas) lo calcula el mapper de FlujoCajaV2 con `repartirVinculadoEnCuotas`
  // — acá solo se agrega el insumo (10.1: una sola función hace el reparto, no se duplica en 2 lugares).
  const vinculadoPorCosto = costoIds.length > 0 ? await getVinculadoNetoPorCosto(costoIds) : {};

  return costos.map((c) => ({
    ...c,
    nombreProyecto: nombreProyectoDesdeMapas(c.negocio?.referencia_id, edificiosMap, clientesMap),
    cuotas: cuotasPorCosto[c.id] || [],
    vinculadoNeto: vinculadoPorCosto[c.id] || 0,
  }));
};

// ─── Fase 4 (vínculo con documentos, 2026-10-04) ───────────────────────────────────────────────
// `costos_proyecto_vinculos` — subledger append-only. Escritura SOLO por las RPCs
// `vincular_documento_costo` / `desvincular_costo` (el guard de la base rechaza cualquier
// INSERT/UPDATE/DELETE directo — 10.190, ver supabase/migrations/20261004a_*). NO toca
// pagos/saldos/libro del proveedor: lo que queda por pagar de la factura sigue proyectándose en
// "Por pagar" como siempre (eje independiente, 10.263-style); esto solo mueve cuánto de la línea
// de costo sigue SIN cubrir en el eje "Proyectos" de Futuros.

// Σ monto_neto VIGENTE por costo_id, para un conjunto de costos — batcheado (10.167), sin N+1.
export const getVinculadoNetoPorCosto = async (costoIds) => {
  if (!costoIds || costoIds.length === 0) return {};
  const { data, error } = await client
    .from("costos_proyecto_vinculos")
    .select("costo_id, monto_neto")
    .in("costo_id", costoIds)
    .is("reversado_at", null);
  if (error) throw error;
  const sumas = {};
  for (const row of data || []) {
    sumas[row.costo_id] = (sumas[row.costo_id] || 0) + Number(row.monto_neto);
  }
  return sumas;
};

// Vínculos VIGENTES de UNA línea de costo, con el folio/fecha del documento resuelto (o la fecha del
// pago, para un anticipo) — insumo de la columna "Vinculado" de la ventana Costos y de
// VinculosCostoModal (Fase 5, 2026-10-04: muestra facturas Y anticipos en la misma lista). Dos pasos
// (nunca un embed PostgREST a través del FK polimórfico documentos_fiscales→origen_id, que no existe
// como relación declarada, 10.139): 1) vínculos + su documento (tipo, origen_id) embebido vía el FK
// real documento_ref→documentos_fiscales; 2) batch de folio/fecha por tipo concreto (sii_recibida /
// factura_externa / movimiento_caja para los anticipos).
export const getVinculosDeCosto = async (costoId) => {
  const { data, error } = await client
    .from("costos_proyecto_vinculos")
    .select(
      "id, tipo, monto_neto, monto_bruto, created_at, movimiento_caja_id, documento:documentos_fiscales(tipo, origen_id)"
    )
    .eq("costo_id", costoId)
    .is("reversado_at", null)
    .order("created_at", { ascending: true });
  if (error) throw error;
  const vinculos = data || [];
  if (vinculos.length === 0) return [];

  const idsSii = vinculos.filter((v) => v.documento?.tipo === "sii_recibida").map((v) => v.documento.origen_id);
  const idsExt = vinculos.filter((v) => v.documento?.tipo === "factura_externa").map((v) => v.documento.origen_id);
  const idsMov = vinculos.filter((v) => v.tipo === "anticipo").map((v) => v.movimiento_caja_id);

  const [siiRes, extRes, movRes] = await Promise.all([
    idsSii.length > 0
      ? client.from("facturas_sii_recibidas").select("id, folio, fecha_emision").in("id", idsSii)
      : Promise.resolve({ data: [] }),
    idsExt.length > 0
      ? client.from("facturas_externas").select("id, numero_documento, fecha_emision").in("id", idsExt)
      : Promise.resolve({ data: [] }),
    idsMov.length > 0
      ? client.from("movimientos_caja").select("id, fecha, monto").in("id", idsMov)
      : Promise.resolve({ data: [] }),
  ]);
  if (siiRes.error) throw siiRes.error;
  if (extRes.error) throw extRes.error;
  if (movRes.error) throw movRes.error;

  const siiMap = Object.fromEntries((siiRes.data || []).map((f) => [f.id, f]));
  const extMap = Object.fromEntries((extRes.data || []).map((f) => [f.id, f]));
  const movMap = Object.fromEntries((movRes.data || []).map((m) => [m.id, m]));

  return vinculos.map((v) => {
    if (v.tipo === "anticipo") {
      const mov = movMap[v.movimiento_caja_id];
      return {
        id: v.id,
        tipo: "anticipo",
        montoNeto: Number(v.monto_neto),
        montoBruto: Number(v.monto_bruto),
        createdAt: v.created_at,
        movimientoCajaId: v.movimiento_caja_id,
        folioLabel: "Anticipo (pago a cuenta)",
        fechaEmision: mov?.fecha || null,
      };
    }
    const origenId = v.documento?.origen_id;
    const esSii = v.documento?.tipo === "sii_recibida";
    const factura = esSii ? siiMap[origenId] : extMap[origenId];
    return {
      id: v.id,
      tipo: "documento",
      montoNeto: Number(v.monto_neto),
      montoBruto: Number(v.monto_bruto),
      createdAt: v.created_at,
      documentoTipo: v.documento?.tipo,
      documentoOrigenId: origenId,
      folioLabel: esSii ? `Folio ${factura?.folio ?? "—"}` : factura?.numero_documento || "Factura externa",
      fechaEmision: factura?.fecha_emision || null,
    };
  });
};

// Resumen batcheado (10.167: sin N+1) de vínculos vigentes para VARIAS líneas de costo a la vez —
// insumo de las columnas "Vinculado"/"Remanente" de la ventana Costos (lista completa del proyecto)
// Y del badge de Estado (Fase 6, 2026-10-04: `calcularEstadoLineaCosto`, abajo). Mismo criterio y
// mismos 2 pasos que `getVinculosDeCosto`, aplicado a todos los costos de una sola pasada en vez de
// un query por fila. `costos` es el array YA cargado por el caller (necesita `id` + `proveedor_id`
// de cada línea — nunca una query extra para resolverlo, 10.167); `pais` es el del negocio.
export const getResumenVinculosPorCostos = async (costos, pais) => {
  const costoIds = (costos || []).map((c) => c.id);
  if (costoIds.length === 0) return {};
  const proveedorPorCosto = Object.fromEntries((costos || []).map((c) => [c.id, c.proveedor_id]));

  const { data, error } = await client
    .from("costos_proyecto_vinculos")
    .select("id, costo_id, tipo, monto_neto, movimiento_caja_id, documento:documentos_fiscales(tipo, origen_id)")
    .in("costo_id", costoIds)
    .is("reversado_at", null)
    .order("created_at", { ascending: true });
  if (error) throw error;
  const vinculos = data || [];
  if (vinculos.length === 0) return {};

  const idsSii = vinculos.filter((v) => v.documento?.tipo === "sii_recibida").map((v) => v.documento.origen_id);
  const idsExt = vinculos.filter((v) => v.documento?.tipo === "factura_externa").map((v) => v.documento.origen_id);
  const idsMov = vinculos
    .filter((v) => v.tipo === "anticipo" && v.movimiento_caja_id != null)
    .map((v) => v.movimiento_caja_id);

  // Estado de pago por documento — fuente ÚNICA (10.1/10.224/10.240): `getSaldosPorFacturaProveedor`
  // es el MISMO motor (ledger FIFO + `resolverSaldo` vía ctxLibro para SII/BHE, R3.2) que usan "Por
  // pagar" (FlujoCajaV2) y el Estado de Cuenta del proveedor — nunca un criterio propio más pobre.
  // UNA llamada por proveedor DISTINTO entre los costos pedidos (nunca por vínculo, 10.167): un
  // proyecto típico tiene 1-3 proveedores. El Map devuelto solo trae facturas con saldo pendiente
  // (> tolerancia) — ausencia de la clave "SII:<id>"/"EXT:<id>" = saldada.
  const provIds = [...new Set(costoIds.map((id) => proveedorPorCosto[id]).filter((x) => x != null))];
  const saldosPorProveedor = {};
  await Promise.all(
    provIds.map(async (provId) => {
      saldosPorProveedor[provId] = await getSaldosPorFacturaProveedor(provId, pais);
    })
  );

  const [siiRes, extRes, movRes] = await Promise.all([
    idsSii.length > 0
      ? client.from("facturas_sii_recibidas").select("id, folio").in("id", idsSii)
      : Promise.resolve({ data: [] }),
    idsExt.length > 0
      ? client.from("facturas_externas").select("id, numero_documento").in("id", idsExt)
      : Promise.resolve({ data: [] }),
    idsMov.length > 0
      ? client.from("movimientos_caja").select("id, fecha").in("id", idsMov)
      : Promise.resolve({ data: [] }),
  ]);
  if (siiRes.error) throw siiRes.error;
  if (extRes.error) throw extRes.error;
  if (movRes.error) throw movRes.error;
  const siiMap = Object.fromEntries((siiRes.data || []).map((f) => [f.id, f]));
  const extMap = Object.fromEntries((extRes.data || []).map((f) => [f.id, f]));
  const movMap = Object.fromEntries((movRes.data || []).map((m) => [m.id, m]));

  const resumen = {};
  for (const v of vinculos) {
    if (!resumen[v.costo_id]) resumen[v.costo_id] = { vinculadoNeto: 0, folios: [], vinculos: [] };
    resumen[v.costo_id].vinculadoNeto += Number(v.monto_neto);
    if (v.tipo === "anticipo") {
      resumen[v.costo_id].folios.push("Anticipo (pago a cuenta)");
      const mov = movMap[v.movimiento_caja_id];
      resumen[v.costo_id].vinculos.push({ tipo: "anticipo", fecha: mov?.fecha || null });
      continue;
    }
    const esSii = v.documento?.tipo === "sii_recibida";
    const origenId = v.documento?.origen_id;
    const factura = esSii ? siiMap[origenId] : extMap[origenId];
    const label = esSii
      ? factura
        ? `Folio ${factura.folio}`
        : undefined
      : factura?.numero_documento || "Factura externa";
    if (label) resumen[v.costo_id].folios.push(label);

    const provId = proveedorPorCosto[v.costo_id];
    const claveLedger = esSii ? `SII:${origenId}` : `EXT:${origenId}`;
    const pagada = origenId != null && !saldosPorProveedor[provId]?.has(claveLedger);
    resumen[v.costo_id].vinculos.push({
      tipo: "documento",
      folio: esSii ? factura?.folio : factura?.numero_documento,
      pagada,
    });
  }
  return resumen;
};

// ─── Fase 6 (badge de Estado por línea, 2026-10-04) ────────────────────────────────────────────
// Estado DERIVADO de una línea de costo — NUNCA se guarda en la base, se recalcula en cada render
// a partir de (a) remanente = monto − vinculadoNeto (misma tolerancia que `calcularDisponibleVinculo`,
// 10.1) y (b) si cada documento vinculado está pagado (según `pagada` que ya resolvió
// `getResumenVinculosPorCostos` con la fuente única del saldo, arriba — esta función NO hace I/O,
// solo compone el resultado, testeable sin mockear supabase).
//
//   - 'previsto'  — sin ningún vínculo vigente (vinculadoNeto <= tolerancia).
//   - 'parcial'   — vinculado > 0 Y remanente > 0 (cobertura incompleta).
//   - 'facturado' — remanente = 0 Y al menos UN documento vinculado sigue con saldo pendiente.
//   - 'pagado'    — remanente = 0 Y (todos los documentos vinculados están saldados, O la cobertura
//                   es solo por anticipos — un anticipo es plata YA desembolsada, pagado por
//                   definición aunque no haya factura todavía).
//
// `vinculos`: [{ tipo:'documento', folio, pagada } | { tipo:'anticipo', fechaLabel }] — `fechaLabel`
// ya formateada dd/mm/aaaa por el caller (mismo util de fecha que el resto de la ventana Costos,
// `formatFecha` en CarteleriaCostosModal/index.js) — esta función no decide formato de fecha/locale.
export const ESTADO_LINEA_COSTO = {
  PREVISTO: "previsto",
  PARCIAL: "parcial",
  FACTURADO: "facturado",
  PAGADO: "pagado",
};

export const calcularEstadoLineaCosto = (montoNeto, vinculadoNeto, vinculos = [], tolerancia = 1) => {
  const vinculado = Number(vinculadoNeto || 0);
  const remanente = Number(montoNeto || 0) - vinculado;

  const documentos = (vinculos || []).filter((v) => v.tipo === "documento");
  const anticipos = (vinculos || []).filter((v) => v.tipo === "anticipo");

  const partes = [];
  const folios = documentos.map((v) => v.folio).filter((f) => f != null && f !== "");
  if (folios.length === 1) partes.push(`Folio ${folios[0]}`);
  else if (folios.length > 1) partes.push(`Folios ${folios.join(" · ")}`);
  if (anticipos.length === 1) partes.push(`Anticipo ${anticipos[0].fechaLabel || ""}`.trim());
  else if (anticipos.length > 1) partes.push(`Anticipos ${anticipos.map((a) => a.fechaLabel).join(" · ")}`);
  const folioDisplay = partes.join(" · ");

  if (vinculado <= tolerancia) return { estado: ESTADO_LINEA_COSTO.PREVISTO, folioDisplay: "" };
  if (remanente > tolerancia) return { estado: ESTADO_LINEA_COSTO.PARCIAL, folioDisplay };

  const hayFacturaPendiente = documentos.some((v) => !v.pagada);
  const estado = hayFacturaPendiente ? ESTADO_LINEA_COSTO.FACTURADO : ESTADO_LINEA_COSTO.PAGADO;
  return { estado, folioDisplay };
};

// Disponible real de UNA factura o UN pago, considerando TODOS los vínculos vigentes que tenga en
// cualquier línea de costo de CUALQUIER proyecto — fuente ÚNICA de "cuánto queda disponible" (10.1),
// usada por los dos pickers de vinculación (`getFacturasParaVincularCosto` para facturas,
// `getPagosACuentaParaAnticipo` para pagos a cuenta/anticipos). El filtro de `reversado_at` se hace
// ACÁ (no solo se confía en el `.is(reversado_at, null)` del lado de la query que trae las filas) —
// defensa en profundidad, y lo que hace esta función testeable sin mockear supabase. Tolerancia 1:
// una diferencia de redondeo no debe dejar "disponible" residual ni mostrar como pendiente algo que
// ya está cubierto. Caso real que motiva esto (2026-10-04): folio 520 de Eladio (Océano), ya
// vinculado 100% a Magdalena Norte (costo_id 41), seguía apareciendo en el picker de otra línea.
export const calcularDisponibleVinculo = (total, vinculosVigentes, campoMonto = "monto_neto", tolerancia = 1) => {
  const vinculado = (vinculosVigentes || [])
    .filter((v) => !v.reversado_at)
    .reduce((acc, v) => acc + Number(v[campoMonto] || 0), 0);
  const disponible = Number(total || 0) - vinculado;
  return disponible <= tolerancia ? 0 : disponible;
};

// Vínculos de `costos_proyecto_vinculos` de un `tipo` ('documento' | 'anticipo'), agrupados por su
// clave (`documento_ref` para facturas, `movimiento_caja_id` para anticipos) — insumo batcheado
// (10.167) de `calcularDisponibleVinculo` para un conjunto de claves a la vez. Trae TODAS las filas
// (vigentes y reversadas): el filtro de vigencia lo hace `calcularDisponibleVinculo`, no esta query.
const getVinculosPorClave = async (tipo, columnaClave, claves) => {
  if (!claves || claves.length === 0) return {};
  const { data, error } = await client
    .from("costos_proyecto_vinculos")
    .select(`${columnaClave}, monto_neto, monto_bruto, reversado_at`)
    .eq("tipo", tipo)
    .in(columnaClave, claves);
  if (error) throw error;
  const porClave = {};
  for (const row of data || []) {
    const clave = row[columnaClave];
    if (!porClave[clave]) porClave[clave] = [];
    porClave[clave].push(row);
  }
  return porClave;
};

// `documento_ref` (documentos_fiscales.id) de cada candidata de factura — SIEMPRE existe (trigger
// `documentos_fiscales_autopoblar` en facturas_sii_recibidas/facturas_externas; verificado 576/576
// reales con ./scripts/db-read.sh, 2026-10-04). Clave del map: `${documentoTipo}:${documentoOrigenId}`.
const getDocumentoRefPorCandidata = async (candidatas) => {
  const idsSii = candidatas.filter((c) => c.documentoTipo === "sii_recibida").map((c) => c.documentoOrigenId);
  const idsExt = candidatas.filter((c) => c.documentoTipo === "factura_externa").map((c) => c.documentoOrigenId);
  const [dfSiiRes, dfExtRes] = await Promise.all([
    idsSii.length > 0
      ? client.from("documentos_fiscales").select("id, origen_id").eq("tipo", "sii_recibida").in("origen_id", idsSii)
      : Promise.resolve({ data: [] }),
    idsExt.length > 0
      ? client.from("documentos_fiscales").select("id, origen_id").eq("tipo", "factura_externa").in("origen_id", idsExt)
      : Promise.resolve({ data: [] }),
  ]);
  if (dfSiiRes.error) throw dfSiiRes.error;
  if (dfExtRes.error) throw dfExtRes.error;
  const refPorClave = {};
  for (const r of dfSiiRes.data || []) refPorClave[`sii_recibida:${r.origen_id}`] = r.id;
  for (const r of dfExtRes.data || []) refPorClave[`factura_externa:${r.origen_id}`] = r.id;
  return refPorClave;
};

// Agrega `disponibleNeto` a cada candidata (total − Σ vinculado vigente, TODAS las líneas de
// CUALQUIER proyecto) y descarta las que ya quedaron en 0 — esconde del picker lo totalmente
// cubierto en otra línea, sin importar en qué proyecto. Las parcialmente vinculadas SIGUEN
// apareciendo, con su disponible real (el modal las muestra con "disponible $X de $Y").
const conDisponibleYOcultarCubiertas = async (candidatas) => {
  if (candidatas.length === 0) return [];
  const refPorClave = await getDocumentoRefPorCandidata(candidatas);
  const refs = Object.values(refPorClave);
  const vinculosPorRef = await getVinculosPorClave("documento", "documento_ref", refs);

  return candidatas
    .map((c) => {
      const ref = refPorClave[`${c.documentoTipo}:${c.documentoOrigenId}`];
      const vinculos = ref != null ? vinculosPorRef[ref] || [] : [];
      return { ...c, disponibleNeto: calcularDisponibleVinculo(c.montoNeto, vinculos, "monto_neto") };
    })
    .filter((c) => c.disponibleNeto > 0);
};

// Facturas candidatas para el selector "Vincular factura" — ya clasificadas, del proveedor de la
// línea de costo, y del tipo correcto según la modalidad del proyecto (10.1: un solo criterio,
// reusado por el selector; la RPC vuelve a validar todo del lado de la base, defensa en profundidad).
// CAPEX → facturas_sii_recibidas clasificadas 'gasto' + facturas_externas clasificadas (cuenta_er
// no nula, no anuladas). OPEX → SOLO facturas_sii_recibidas clasificadas 'activo' (una factura
// externa nunca genera activo). NC (tipo_dte=61) excluida siempre. Fix 2026-10-04: esconde las
// facturas ya cubiertas al 100% en CUALQUIER otra línea/proyecto (`conDisponibleYOcultarCubiertas`,
// arriba) — antes ofrecía facturas que la RPC de todos modos iba a rechazar (candado de Fase 4/5).
export const getFacturasParaVincularCosto = async ({ pais, proveedorId, modalidad }) => {
  if (!pais || !proveedorId || !modalidad) return [];

  const siiQuery = client
    .from("facturas_sii_recibidas")
    .select("id, folio, fecha_emision, monto_neto, tipo_contable")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .neq("tipo_dte", 61)
    .eq("tipo_contable", modalidad === "OPEX" ? "activo" : "gasto")
    .order("fecha_emision", { ascending: false });

  const { data: siiData, error: siiError } = await siiQuery;
  if (siiError) throw siiError;
  const candidatasSii = (siiData || []).map((f) => ({
    documentoTipo: "sii_recibida",
    documentoOrigenId: f.id,
    label: `Folio ${f.folio}`,
    fechaEmision: f.fecha_emision,
    montoNeto: Number(f.monto_neto),
  }));

  if (modalidad === "OPEX") return conDisponibleYOcultarCubiertas(candidatasSii); // externas nunca generan activo (10.1)

  const { data: extData, error: extError } = await client
    .from("facturas_externas")
    .select("id, numero_documento, fecha_emision, monto_neto, cuenta_er, estado")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .not("cuenta_er", "is", null)
    .neq("estado", "anulada")
    .order("fecha_emision", { ascending: false });
  if (extError) throw extError;
  const candidatasExt = (extData || []).map((f) => ({
    documentoTipo: "factura_externa",
    documentoOrigenId: f.id,
    label: f.numero_documento || `Factura externa #${f.id}`,
    fechaEmision: f.fecha_emision,
    montoNeto: Number(f.monto_neto),
  }));

  const candidatas = [...candidatasSii, ...candidatasExt].sort((a, b) =>
    (b.fechaEmision || "").localeCompare(a.fechaEmision || "")
  );
  return conDisponibleYOcultarCubiertas(candidatas);
};

// RPC `vincular_documento_costo` (único writer, junto con `desvincularCosto`, de
// costos_proyecto_vinculos). `montoBruto` se calcula del lado del caller con `totalConIvaCosto`/
// `brutoCapex` (10.1/10.195) — la RPC no recalcula IVA, solo guarda lo que llega.
export const vincularDocumentoCosto = async ({
  costoId,
  documentoTipo,
  documentoOrigenId,
  montoNeto,
  montoBruto,
  usuarioId = null,
}) => {
  const { data, error } = await client.rpc("vincular_documento_costo", {
    p_costo_id: costoId,
    p_documento_tipo: documentoTipo,
    p_documento_origen_id: documentoOrigenId,
    p_monto_neto: montoNeto,
    p_monto_bruto: montoBruto,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

// RPC `desvincular_costo` (reversa por evento, 10.248 — nunca borra la fila). Sirve TAMBIÉN para
// desvincular un vínculo tipo='anticipo' (Fase 5, 2026-10-04) — la RPC ya generaliza sin cambios: con
// `documento_tipo`/`documento_ref` NULL en esa fila, la rama de destageo de activo (OPEX) simplemente
// no encuentra nada que hacer y no se ejecuta.
export const desvincularCosto = async ({ vinculoId, usuarioId = null }) => {
  const { data, error } = await client.rpc("desvincular_costo", {
    p_vinculo_id: vinculoId,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

// ─── Fase 5 (anticipo pre-factura, 2026-10-04) ─────────────────────────────────────────────────
// `costos_proyecto_vinculos` tipo='anticipo' — vincula un pago a cuenta de proveedor (sin aplicar a
// ninguna factura todavía) como cobertura provisoria de una línea de costo. Escritura SOLO por
// `vincular_anticipo_costo` (y la reversa automática dentro de `vincular_documento_costo`, del lado
// de la base) — el guard de la base rechaza cualquier INSERT/UPDATE/DELETE directo (10.190, ver
// supabase/migrations/20261004b_*).

// Pagos a cuenta de un proveedor, elegibles para usarse como anticipo de una línea de costo: mismo
// proveedor, mismo país (Chile only — 10.267), `categoria_pago='pago_a_cuenta'`, no anulados, y que
// NO estén ya aplicados a ninguna factura (libro `aplicaciones_saldo`, `origen_tipo='anticipo'` — el
// mismo chequeo por EXISTENCIA que hace la RPC, defensa en profundidad del lado del picker). El
// "disponible" (total − Σ anticipos vigentes de ese mismo pago en CUALQUIER línea, 10.1) se calcula
// con la MISMA fuente única que usa el picker de facturas (`calcularDisponibleVinculo`, arriba) —
// nunca un cálculo paralelo.
export const getPagosACuentaParaAnticipo = async ({ pais, proveedorId }) => {
  if (!pais || !proveedorId) return [];
  if (pais !== "Chile") return []; // 10.267: Perú fuera de esta fase

  const { data: movs, error: movError } = await client
    .from("movimientos_caja")
    .select("id, fecha, monto, moneda")
    .eq("pais", pais)
    .eq("entidad_tipo", "proveedor")
    .eq("entidad_id", proveedorId)
    .eq("categoria_pago", "pago_a_cuenta")
    .neq("estado", "anulado")
    .order("fecha", { ascending: false });
  if (movError) throw movError;
  const pagos = movs || [];
  if (pagos.length === 0) return [];

  const ids = pagos.map((m) => m.id);
  const [aplicadosRes, vinculosPorMov] = await Promise.all([
    client.from("aplicaciones_saldo").select("origen_id").eq("origen_tipo", "anticipo").in("origen_id", ids),
    getVinculosPorClave("anticipo", "movimiento_caja_id", ids),
  ]);
  if (aplicadosRes.error) throw aplicadosRes.error;

  const idsYaAplicados = new Set((aplicadosRes.data || []).map((a) => a.origen_id));

  return pagos
    .filter((m) => !idsYaAplicados.has(m.id))
    .map((m) => ({
      movimientoCajaId: m.id,
      fecha: m.fecha,
      montoBruto: Number(m.monto),
      moneda: m.moneda,
      disponibleBruto: calcularDisponibleVinculo(Number(m.monto), vinculosPorMov[m.id] || [], "monto_bruto"),
    }))
    .filter((m) => m.disponibleBruto > 0);
};

// Líneas de costo VIGENTES (no eliminadas) de un proveedor con remanente > 0 — dirección inversa de
// `getPagosACuentaParaAnticipo` (esa parte de un costo y lista pagos; esta parte de un pago — el
// paso opcional de Banco, "¿corresponde a un proyecto de Cartelería?" — y lista costos). Mismo
// criterio de remanente que `getCostosProyectoFuturos`/Fase 4: monto NETO de la línea − Σ vinculado
// vigente (`getVinculadoNetoPorCosto`, 10.1 — nunca se reimplementa la suma). Chile only (10.267).
export const getCostosProyectoConRemanentePorProveedor = async ({ pais, proveedorId }) => {
  if (!pais || !proveedorId) return [];
  if (pais !== "Chile") return []; // 10.267: Perú fuera de esta fase

  const { data: costos, error } = await client
    .from("costos_proyecto")
    .select("id, negocio_id, concepto, monto, moneda, exento_iva, negocio:negocios_carteleria(id, referencia_id)")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .eq("eliminado", false);
  if (error) throw error;
  const lineas = costos || [];
  if (lineas.length === 0) return [];

  const costoIds = lineas.map((c) => c.id);
  const [vinculadoPorCosto, edificiosRes, clientesRes] = await Promise.all([
    getVinculadoNetoPorCosto(costoIds),
    client.from("edificios").select("id, edificio").eq("pais", pais),
    client.from("clientes").select("id, nombre").eq("pais", pais),
  ]);
  if (edificiosRes.error) throw edificiosRes.error;
  if (clientesRes.error) throw clientesRes.error;
  const edificiosMap = Object.fromEntries((edificiosRes.data || []).map((e) => [e.id, e.edificio]));
  const clientesMap = Object.fromEntries((clientesRes.data || []).map((c) => [c.id, c.nombre]));

  return lineas
    .map((c) => {
      const remanenteNeto = Number(c.monto) - (vinculadoPorCosto[c.id] || 0);
      return {
        id: c.id,
        negocioId: c.negocio_id,
        nombreProyecto: nombreProyectoDesdeMapas(c.negocio?.referencia_id, edificiosMap, clientesMap),
        concepto: c.concepto,
        monto: Number(c.monto),
        moneda: c.moneda,
        exentoIva: c.exento_iva,
        remanenteNeto,
      };
    })
    .filter((c) => c.remanenteNeto > 0.5); // tolerancia 1, igual criterio que el resto de Fase 4/5
};

// Fase 2a/2b (proyección/factura/pago, 2026-10-05) — dirección factura→costos candidatos para el
// modal único (§3.4 del diseño de Fase 2, pide una función `getCostosParaVincularFactura` molde
// `getActivosParaVincularFactura`). Verificado por lectura directa de `vincular_documento_costo`
// (10.28): el candado de proveedor ahí es `v_doc_proveedor IS DISTINCT FROM v_costo_proveedor` —
// match EXACTO, sin el COALESCE "o sin proveedor asignado" que sí tiene `vincular_factura_activo`
// para activos. Con ese criterio real, el candidato es EXACTAMENTE el que ya calcula
// `getCostosProyectoConRemanentePorProveedor` (mismo proveedor, remanente > 0, no eliminado) — esa
// función nació para la dirección pago→costo (Fase 5, anticipos), pero su query es la misma que
// necesita esta dirección nueva (factura→costo). Alias con el nombre que pide el diseño en vez de
// duplicar la query (10.1/10.27 — nunca reimplementar lo que ya existe).
export const getCostosParaVincularFactura = getCostosProyectoConRemanentePorProveedor;

// RPC `vincular_anticipo_costo` (único writer, junto con `vincular_documento_costo`'s reversa y
// `desvincularCosto`, de filas tipo='anticipo'). `montoNeto` se calcula del lado del caller con
// `netoDesdeBrutoCapex`/`brutoCapex` (10.1/10.195) a partir del BRUTO que el usuario elige vincular —
// la RPC no recalcula IVA, solo guarda lo que llega.
export const vincularAnticipoCosto = async ({ costoId, movimientoCajaId, montoNeto, montoBruto, usuarioId = null }) => {
  const { data, error } = await client.rpc("vincular_anticipo_costo", {
    p_costo_id: costoId,
    p_movimiento_caja_id: movimientoCajaId,
    p_monto_neto: montoNeto,
    p_monto_bruto: montoBruto,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

// Reparto del monto ya vinculado (NETO) sobre las cuotas de una línea de costo, de la MÁS VIEJA a
// la más nueva (oldest-first) — molde "compromiso de pago §5.2" citado en el diseño (§2.3 punto 3),
// reescrito acá como función pura propia porque ese molde ya no existe centralizado (retirado con la
// redefinición de compromiso de pago, 10.286) y ninguna otra pieza del proyecto reparte un total
// cubierto sobre un array de cuotas con esta forma exacta ({numero_cuota, monto_cuota}). Nunca muta
// el array de entrada. Devuelve, por cuota, cuánto NETO queda remanente (>= 0, con tolerancia 1
// tratada como 0 — una cuota "prácticamente cubierta" no debe aparecer en Futuros por $1 de redondeo).
// El llamador (mapper de Futuros) convierte el remanente NETO a BRUTO con brutoCapex antes de
// proyectarlo (10.195: Futuros proyecta caja, siempre bruto).
export const repartirVinculadoEnCuotas = (cuotas, totalVinculadoNeto, tolerancia = 1) => {
  const ordenadas = [...(cuotas || [])].sort((a, b) => a.numero_cuota - b.numero_cuota);
  let restante = Number(totalVinculadoNeto) || 0;
  return ordenadas.map((cu) => {
    const montoCuota = Number(cu.monto_cuota);
    const absorbido = Math.min(Math.max(restante, 0), montoCuota);
    restante -= absorbido;
    const remanenteNeto = montoCuota - absorbido;
    return { ...cu, remanenteNeto: remanenteNeto <= tolerancia ? 0 : remanenteNeto };
  });
};
